// frontend/src/widgets/TrafficIntelligenceWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-10
// Updated:      2026-09-09 15:42 BST
//
// Description:  Traffic Intelligence widget.
//               - Location pinning via LocationEditModal.
//               - Reads/writes shared pinned location so Weather, Flood and Traffic widgets stay in sync.
//               - Fetches /api/traffic/intelligence using settled pinned coordinates only.
//               - Renders operational-impact-first summary rather than raw upstream telemetry.
//               - Uses inline SVG incident/status icons with theme-safe colour treatment.
//               - Responsive stacked layout for narrow/mobile widths.
//
// Mobile + Performance Pass (2026-09-09):
//               - Preserves the existing traffic-intelligence endpoint contract,
//                 persist=true behaviour, cache-bust semantics and shared-location sync.
//               - Adds a short-lived 90-second session cache keyed by settled coordinates
//                 so repeat widget mounts at the same location can render immediately
//                 without paying the intelligence request cost again.
//               - Manual Refresh always performs a real network request.
//               - Cache Bust clears the client cache and still sends cache_bust=1.
//               - Adds in-flight request de-duplication and stale-response suppression so
//                 rapid location changes cannot wastefully overwrite newer results.
//               - Lazy-loads LocationEditModal only when the map picker is opened.
//               - Replaces per-resize state updates with matchMedia breakpoint-change events.
//               - Incident rows use content-visibility for cheaper rendering of large result sets.
//               - Mobile uses one complete surfaced-incident row per internal vertical scroll/snap.
//               - Outer mobile widget height uses the proven Vision two-pass fixed-host sizing pattern.
//               - Mobile buttons become full-width touch targets; long chips/text wrap safely.
//
// Operational Notes:
//               - The first uncached intelligence request can still take as long as the backend/
//                 upstream provider requires. This pass removes avoidable repeat-load/frontend cost.
//               - No backend route, query parameter, response shape or intelligence logic is changed.

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import api from "../api/axiosInstance";
import { useTheme } from "../context/ThemeContext.jsx";

const LazyLocationEditModal = React.lazy(() =>
  import("../components/LocationEditModal.jsx")
);

const SHARED_LOCATION_KEY = "jobsPinnedLocation";
const SHARED_LOCATION_EVENT = "jobs-pinned-location-changed";

const TRAFFIC_SESSION_CACHE_PREFIX = "trafficIntelligence:v1:";
const TRAFFIC_SESSION_CACHE_TTL_MS = 90 * 1000;

const getTrafficCoordKey = (loc) => {
  const lat = Number(loc?.lat);
  const lon = Number(loc?.lon);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return "";
  }

  return `${lat.toFixed(6)}|${lon.toFixed(6)}`;
};

const getTrafficSessionCacheKey = (loc) => {
  const coordKey = getTrafficCoordKey(loc);
  return coordKey
    ? `${TRAFFIC_SESSION_CACHE_PREFIX}${coordKey}|Drone Survey`
    : "";
};

const readTrafficSessionCache = (loc) => {
  const cacheKey = getTrafficSessionCacheKey(loc);
  if (!cacheKey) return null;

  try {
    const raw = sessionStorage.getItem(cacheKey);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    const savedAt = Number(parsed?.saved_at || 0);

    if (
      !savedAt ||
      Date.now() - savedAt > TRAFFIC_SESSION_CACHE_TTL_MS ||
      !parsed?.data
    ) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};

const writeTrafficSessionCache = (loc, data) => {
  const cacheKey = getTrafficSessionCacheKey(loc);
  if (!cacheKey || !data) return;

  try {
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        saved_at: Date.now(),
        data,
      })
    );
  } catch {
    // Ignore storage/quota failures. Network behaviour remains available.
  }
};

const clearTrafficSessionCache = (loc) => {
  const cacheKey = getTrafficSessionCacheKey(loc);
  if (!cacheKey) return;

  try {
    sessionStorage.removeItem(cacheKey);
  } catch {
    // Ignore storage failures.
  }
};

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const fmtNum = (n, dp = 1) => {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return x.toFixed(dp);
};

const safeText = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
};

const formatApiError = (err) => {
  const status = err?.response?.status;
  const data = err?.response?.data;

  if (data == null) return status ? `${status} — Request failed` : err?.message || "Request failed";
  if (typeof data === "string") return status ? `${status} — ${data}` : data;

  const detail = data?.detail ?? data?.message ?? data;
  try {
    const text = typeof detail === "string" ? detail : JSON.stringify(detail);
    return status ? `${status} — ${text}` : text;
  } catch {
    return status ? `${status} — Error` : "Error";
  }
};

const formatDateTimeGB = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
  } catch {
    return "—";
  }
};

const formatSpeed = (value, unit = "mph") => {
  if (!isFiniteNumber(value)) return "—";
  return `${Math.round(Number(value))} ${unit}`;
};

const formatDelay = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "string" && value.trim()) return value.trim();

  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;

  const sec = Math.max(0, Math.round(n));
  if (sec < 60) return `${sec} sec`;

  const mins = Math.round(sec / 60);
  if (mins < 60) return `${mins} min`;

  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs} hr ${rem} min` : `${hrs} hr`;
};

const impactToneMap = {
  ok: {
    bg: "rgba(16, 185, 129, 0.10)",
    border: "rgba(16, 185, 129, 0.30)",
    text: "#047857",
    label: "No Material Impact",
  },
  monitor: {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.28)",
    text: "#1d4ed8",
    label: "Monitor Conditions",
  },
  warning: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.30)",
    text: "#b45309",
    label: "Review Before Deploy",
  },
  critical: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.35)",
    text: "#b91c1c",
    label: "Likely Operational Impact",
  },
  unknown: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.25)",
    text: "#4b5563",
    label: "Unknown",
  },
};

const chipStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  maxWidth: "100%",
  padding: "6px 12px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 900,
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  background: tone.bg,
  border: `1px solid ${tone.border}`,
  color: tone.text,
});

const readSharedLocation = () => {
  try {
    const raw = localStorage.getItem(SHARED_LOCATION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const lat = Number(parsed?.lat);
    const lon = Number(parsed?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return {
      name: parsed?.name || "Pinned Location",
      lat,
      lon,
    };
  } catch {
    return null;
  }
};

const broadcastSharedLocation = (loc) => {
  window.dispatchEvent(
    new CustomEvent(SHARED_LOCATION_EVENT, {
      detail: {
        name: loc?.name || "Pinned Location",
        lat: Number(loc?.lat),
        lon: Number(loc?.lon),
        updated_at: new Date().toISOString(),
      },
    })
  );
};

const writeSharedLocation = (loc) => {
  const payload = {
    name: loc?.name || "Pinned Location",
    lat: Number(loc?.lat),
    lon: Number(loc?.lon),
    updated_at: new Date().toISOString(),
  };

  try {
    localStorage.setItem(SHARED_LOCATION_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }

  broadcastSharedLocation(payload);
};

const sameLocation = (a, b) =>
  Number(a?.lat) === Number(b?.lat) &&
  Number(a?.lon) === Number(b?.lon) &&
  String(a?.name || "") === String(b?.name || "");

const extractLocationFromAny = (obj) => {
  if (!obj || typeof obj !== "object") return null;

  const lat =
    obj?.lat ??
    obj?.latitude ??
    obj?.location?.lat ??
    obj?.location?.latitude ??
    null;

  const lon =
    obj?.lon ??
    obj?.lng ??
    obj?.longitude ??
    obj?.location?.lon ??
    obj?.location?.lng ??
    obj?.location?.longitude ??
    null;

  const name =
    obj?.name ??
    obj?.label ??
    obj?.title ??
    obj?.location?.name ??
    "Pinned Location";

  if (!isFiniteNumber(lat) || !isFiniteNumber(lon)) return null;

  return {
    name: String(name || "Pinned Location"),
    lat: Number(lat),
    lon: Number(lon),
  };
};

const resolveCreatedLocation = async (created) => {
  const direct = extractLocationFromAny(created);
  if (direct) return direct;

  const id = created?.id ?? created?.location_id ?? created?.location?.id ?? null;
  if (!id) return null;

  const r = await api.get(`/locations/${id}`);
  return extractLocationFromAny(r?.data);
};

const TrafficSignalIcon = ({ status = "unknown", size = 56 }) => {
  const tone = impactToneMap[status] || impactToneMap.unknown;
  const fill = tone.text;

  if (status === "critical") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <path d="M20 44L32 18L44 44H20Z" fill={fill} />
        <rect x="30" y="26" width="4" height="10" rx="2" fill="#fff" />
        <circle cx="32" cy="40" r="2.2" fill="#fff" />
      </svg>
    );
  }

  if (status === "warning") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <rect x="18" y="16" width="28" height="32" rx="7" fill={fill} />
        <circle cx="32" cy="25" r="4.2" fill="#fff" opacity="0.98" />
        <circle cx="32" cy="32" r="4.2" fill="#fff" opacity="0.75" />
        <circle cx="32" cy="39" r="4.2" fill="#fff" opacity="0.42" />
      </svg>
    );
  }

  if (status === "monitor") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <rect x="17" y="18" width="30" height="28" rx="8" fill={fill} />
        <path d="M23 35C25.8 30.8 29.4 28.7 33.6 28.7C37.3 28.7 40.5 30.1 43 33" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        <circle cx="25" cy="39" r="2" fill="#fff" />
        <circle cx="39" cy="39" r="2" fill="#fff" />
      </svg>
    );
  }

  if (status === "ok") {
    return (
      <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
        <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
        <path d="M16 38L24 30L31 36L42 24L48 29" fill="none" stroke={fill} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M45 23H49V27" fill="none" stroke={fill} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r="28" fill={tone.bg} stroke={tone.border} strokeWidth="2" />
      <circle cx="32" cy="24" r="3" fill={fill} />
      <circle cx="32" cy="32" r="3" fill={fill} />
      <circle cx="32" cy="40" r="3" fill={fill} />
    </svg>
  );
};

const IncidentTypeIcon = ({ incident, size = 18 }) => {
  const events = Array.isArray(incident?.event_descriptions)
    ? incident.event_descriptions.map((x) => String(x || "").toLowerCase())
    : [];

  const has = (txt) => events.some((e) => e.includes(txt));
  const tone =
    has("closed")
      ? impactToneMap.critical
      : has("lane")
      ? impactToneMap.warning
      : has("stationary")
      ? impactToneMap.warning
      : has("queuing")
      ? impactToneMap.monitor
      : impactToneMap.unknown;

  const stroke = tone.text;
  const bg = tone.bg;
  const border = tone.border;

  if (has("closed")) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill={bg} stroke={border} strokeWidth="1.5" />
        <path d="M8 8L16 16M16 8L8 16" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    );
  }

  if (has("stationary")) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill={bg} stroke={border} strokeWidth="1.5" />
        <rect x="6" y="7" width="12" height="7" rx="2" fill={stroke} />
        <circle cx="9" cy="16.5" r="1.5" fill={stroke} />
        <circle cx="15" cy="16.5" r="1.5" fill={stroke} />
      </svg>
    );
  }

  if (has("queuing")) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill={bg} stroke={border} strokeWidth="1.5" />
        <path d="M6 14H15" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" />
        <path d="M12 10L16 14L12 18" fill="none" stroke={stroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (has("broken down") || has("accident") || has("collision")) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill={bg} stroke={border} strokeWidth="1.5" />
        <path d="M8 7L10.5 10L8 13" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 7L13.5 10L16 13" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9.5 16H14.5" stroke={stroke} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill={bg} stroke={border} strokeWidth="1.5" />
      <circle cx="12" cy="12" r="3" fill={stroke} />
    </svg>
  );
};

const IncidentRow = ({ incident, bodyFont, rowRef = null }) => {
  const events = Array.isArray(incident?.event_descriptions) ? incident.event_descriptions.filter(Boolean) : [];
  const roads = Array.isArray(incident?.road_numbers) ? incident.road_numbers.filter(Boolean) : [];

  return (
    <div
      ref={rowRef}
      className="vx-traffic-incident-row"
      style={{
        borderRadius: 14,
        border: "1px solid rgba(0,0,0,0.08)",
        background: "#fff",
        padding: 12,
        minWidth: 0,
        overflowWrap: "anywhere",
        contentVisibility: "auto",
        containIntrinsicSize: "140px",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ marginTop: 1 }}>
          <IncidentTypeIcon incident={incident} size={20} />
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 900,
              color: "rgba(0,0,0,0.82)",
              lineHeight: 1.25,
              fontFamily: bodyFont,
            }}
          >
            {events.length ? events.join(" · ") : "Live traffic incident"}
          </div>

          <div style={{ fontSize: 12, color: "rgba(0,0,0,0.64)", marginTop: 4, lineHeight: 1.45 }}>
            {safeText(incident?.from, "Unknown")} → {safeText(incident?.to, "Unknown")}
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
            {roads.length ? (
              <span
                style={chipStyle({
                  bg: "rgba(17, 24, 39, 0.05)",
                  border: "rgba(17, 24, 39, 0.12)",
                  text: "#111827",
                })}
              >
                {roads.join(", ")}
              </span>
            ) : null}

            {incident?.flow_distance_display_text ? (
              <span
                style={chipStyle({
                  bg: "rgba(99, 102, 241, 0.08)",
                  border: "rgba(99, 102, 241, 0.22)",
                  text: "#4338ca",
                })}
              >
                Corridor: {incident.flow_distance_display_text}
              </span>
            ) : incident?.distance_display_text ? (
              <span
                style={chipStyle({
                  bg: "rgba(99, 102, 241, 0.08)",
                  border: "rgba(99, 102, 241, 0.22)",
                  text: "#4338ca",
                })}
              >
                Distance: {incident.distance_display_text}
              </span>
            ) : null}

            {incident?.delay_display_text ? (
              <span
                style={chipStyle({
                  bg: "rgba(245, 158, 11, 0.09)",
                  border: "rgba(245, 158, 11, 0.22)",
                  text: "#92400e",
                })}
              >
                Delay: {incident.delay_display_text}
              </span>
            ) : null}
          </div>

          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.48)", marginTop: 8 }}>
            {formatDateTimeGB(incident?.start_time)} → {formatDateTimeGB(incident?.end_time)}
          </div>
        </div>
      </div>
    </div>
  );
};

const TrafficIntelligenceWidget = () => {
  const { theme } = useTheme();

  const widgetRootRef = useRef(null);
  const incidentsViewportRef = useRef(null);
  const firstIncidentRowRef = useRef(null);
  const activeRequestKeyRef = useRef("");
  const requestSequenceRef = useRef(0);

  const [openPicker, setOpenPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [resp, setResp] = useState(null);
  const [isNarrow, setIsNarrow] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(max-width: 899px)").matches;
  });

  const [loc, setLoc] = useState(
    () =>
      readSharedLocation() || {
        name: "Pinned Location",
        lat: 51.5074,
        lon: -0.1278,
      }
  );

  const headerFont = useMemo(() => theme?.font_header || "var(--font-header, Arial)", [theme]);
  const bodyFont = useMemo(() => theme?.font_body || "var(--font-body, Arial)", [theme]);
  const accent = useMemo(() => theme?.accent_color || "var(--accent-color, #ff7a00)", [theme]);

  const btnBase = {
    border: "none",
    borderRadius: 999,
    padding: "10px 16px",
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: bodyFont,
    transition: "transform 0.06s ease, filter 0.15s ease",
    userSelect: "none",
  };

  const mkBtnHandlers = (normal, hover, click) => ({
    onMouseEnter: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.backgroundColor = normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
    onMouseDown: (e) => {
      e.currentTarget.style.backgroundColor = click || hover || normal;
      e.currentTarget.style.transform = "translateY(1px)";
    },
    onMouseUp: (e) => {
      e.currentTarget.style.backgroundColor = hover || normal;
      e.currentTarget.style.transform = "translateY(0px)";
    },
  });

  const actionBtnStyle = useMemo(() => {
    const normal = theme?.button_action?.normal || "#1f7a8c";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont]);

  const generalBtnStyle = useMemo(() => {
    const normal = theme?.button_general?.normal || "#222";
    return { ...btnBase, backgroundColor: normal, color: "#fff" };
  }, [theme, bodyFont]);

  const actionHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_action?.normal || "#1f7a8c",
        theme?.button_action?.hover || "#256f7f",
        theme?.button_action?.click || "#1b5e6c"
      ),
    [theme]
  );

  const generalHandlers = useMemo(
    () =>
      mkBtnHandlers(
        theme?.button_general?.normal || "#222",
        theme?.button_general?.hover || "#333",
        theme?.button_general?.click || "#111"
      ),
    [theme]
  );

  const inputStyle = useMemo(
    () => ({
      height: 42,
      padding: "10px 14px",
      border: "1px solid rgba(0,0,0,0.12)",
      borderRadius: 16,
      width: "100%",
      background: "#fff",
      boxSizing: "border-box",
      fontFamily: bodyFont,
      fontSize: 14,
      outline: "none",
    }),
    [bodyFont]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 899px)");
    const onBreakpointChange = (event) => {
      setIsNarrow(event.matches);
    };

    setIsNarrow(mediaQuery.matches);
    mediaQuery.addEventListener?.("change", onBreakpointChange);

    return () => {
      mediaQuery.removeEventListener?.("change", onBreakpointChange);
    };
  }, []);

  const fetchTraffic = useCallback(
    async ({ cacheBust = false } = {}) => {
      if (!isFiniteNumber(loc?.lat) || !isFiniteNumber(loc?.lon)) {
        setResp(null);
        setError("Pinned location is invalid.");
        return;
      }

      const requestLocation = {
        name: loc?.name || "Pinned Location",
        lat: Number(loc.lat),
        lon: Number(loc.lon),
      };

      const coordKey = getTrafficCoordKey(requestLocation);
      const requestKey = `${coordKey}|${cacheBust ? "cache-bust" : "normal"}`;

      if (!cacheBust && activeRequestKeyRef.current === requestKey) {
        return;
      }

      if (cacheBust) {
        clearTrafficSessionCache(requestLocation);
      }

      const requestSequence = requestSequenceRef.current + 1;
      requestSequenceRef.current = requestSequence;
      activeRequestKeyRef.current = requestKey;

      setBusy(true);
      setError("");

      try {
        const r = await api.get("/traffic/intelligence", {
          params: {
            lat: requestLocation.lat,
            lon: requestLocation.lon,
            job_type_label: "Drone Survey",
            persist: true,
            include_raw: false,
            ...(cacheBust ? { cache_bust: 1 } : {}),
          },
        });

        if (requestSequence !== requestSequenceRef.current) {
          return;
        }

        const nextData = r?.data || null;
        setResp(nextData);

        if (nextData) {
          writeTrafficSessionCache(requestLocation, nextData);
        }
      } catch (e) {
        if (requestSequence !== requestSequenceRef.current) {
          return;
        }

        setResp(null);
        setError(formatApiError(e));
      } finally {
        if (requestSequence === requestSequenceRef.current) {
          activeRequestKeyRef.current = "";
          setBusy(false);
        }
      }
    },
    [loc?.lat, loc?.lon, loc?.name]
  );

  const lastCoordRef = useRef("");
  useEffect(() => {
    const coordKey = getTrafficCoordKey(loc);
    if (!coordKey || coordKey === lastCoordRef.current) return;

    lastCoordRef.current = coordKey;

    const cached = readTrafficSessionCache(loc);
    if (cached) {
      setResp(cached);
      setError("");
      setBusy(false);
      return;
    }

    fetchTraffic({ cacheBust: false });
  }, [fetchTraffic, loc]);

  useEffect(() => {
    const shared = readSharedLocation();
    if (shared) {
      setLoc((prev) => (sameLocation(prev, shared) ? prev : shared));
    }

    const onStorage = (event) => {
      if (event.key !== SHARED_LOCATION_KEY) return;
      const sharedLoc = readSharedLocation();
      if (!sharedLoc) return;
      setLoc((prev) => (sameLocation(prev, sharedLoc) ? prev : sharedLoc));
    };

    const onSharedLocation = (event) => {
      const nextLoc = event?.detail;
      if (!nextLoc || !isFiniteNumber(nextLoc?.lat) || !isFiniteNumber(nextLoc?.lon)) return;
      setLoc((prev) => (sameLocation(prev, nextLoc) ? prev : nextLoc));
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener(SHARED_LOCATION_EVENT, onSharedLocation);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SHARED_LOCATION_EVENT, onSharedLocation);
    };
  }, []);

  const pinnedLabel = useMemo(() => {
    if (loc?.name && String(loc.name).trim()) return String(loc.name).trim();
    return "Pinned Location";
  }, [loc]);

  const liveBadge = useMemo(() => {
    const ok = !busy && !error && resp?.ok;
    const bg = ok ? "#e8f5e9" : error ? "#ffebee" : "#e3f2fd";
    const bd = ok ? "#c8e6c9" : error ? "#ffcdd2" : "#bbdefb";
    const fg = ok ? "#1b5e20" : error ? "#b71c1c" : "#0d47a1";
    const text = ok ? "Live" : error ? "Error" : busy ? "Loading" : "Idle";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 900,
          background: bg,
          border: `1px solid ${bd}`,
          color: fg,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 99, background: fg, display: "inline-block" }} />
        {text}
      </span>
    );
  }, [busy, error, resp]);

  const assessmentStatus = String(resp?.assessment?.status || "unknown").toLowerCase();
  const statusTone = impactToneMap[assessmentStatus] || impactToneMap.unknown;

  const incidents = Array.isArray(resp?.incidents) ? resp.incidents : [];
  const traffic = resp?.traffic || {};
  const assessment = resp?.assessment || {};
  const unit = String(traffic?.current_speed || "").length >= 0 && String(resp?.settings_source || "") ? "mph" : "mph";

  useLayoutEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const root = widgetRootRef.current;
    const viewport = incidentsViewportRef.current;
    const firstIncident = firstIncidentRowRef.current;

    if (!root) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 767px)");

    let animationFrame = 0;
    let hostRecord = null;

    const restoreHost = () => {
      if (!hostRecord?.node) return;

      hostRecord.node.style.height = hostRecord.height;
      hostRecord.node.style.minHeight = hostRecord.minHeight;
      hostRecord = null;
    };

    const findFixedMobileHost = () => {
      const rootRect = root.getBoundingClientRect();
      const candidates = [];

      let node = root.parentElement;
      let depth = 0;

      while (node && node !== document.body && depth < 6) {
        const rect = node.getBoundingClientRect();
        const inlineHeight = String(node.style?.height || "").trim();

        const widthMatches =
          Math.abs(rect.width - rootRect.width) < 90;

        const hasFixedInlineHeight =
          inlineHeight &&
          inlineHeight !== "auto" &&
          inlineHeight !== "100%" &&
          inlineHeight !== "100vh";

        if (widthMatches && hasFixedInlineHeight) {
          candidates.push(node);
        }

        node = node.parentElement;
        depth += 1;
      }

      return candidates.length ? candidates[0] : null;
    };

    const applyMobileSizing = () => {
      cancelAnimationFrame(animationFrame);
      restoreHost();

      const currentViewport = incidentsViewportRef.current;

      if (currentViewport) {
        currentViewport.style.height = "";
        currentViewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";

      if (!mediaQuery.matches) {
        return;
      }

      animationFrame = requestAnimationFrame(() => {
        const currentFirstIncident = firstIncidentRowRef.current;
        const nextViewport = incidentsViewportRef.current;

        if (currentFirstIncident && nextViewport) {
          const incidentHeight = Math.ceil(
            Math.max(
              currentFirstIncident.getBoundingClientRect().height || 0,
              currentFirstIncident.scrollHeight || 0
            )
          );

          if (Number.isFinite(incidentHeight) && incidentHeight > 0) {
            nextViewport.style.height = `${incidentHeight}px`;
            nextViewport.style.minHeight = `${incidentHeight}px`;
          }
        }

        root.style.height = "auto";
        root.style.minHeight = "0px";

        animationFrame = requestAnimationFrame(() => {
          const host = findFixedMobileHost();

          if (!host) {
            return;
          }

          hostRecord = {
            node: host,
            height: host.style.height,
            minHeight: host.style.minHeight,
          };

          const hostRect = host.getBoundingClientRect();
          const rootRect = root.getBoundingClientRect();

          const wrapperChromeHeight = Math.max(
            0,
            rootRect.top - hostRect.top
          );

          const requiredHostHeight = Math.ceil(
            Math.max(
              host.scrollHeight,
              wrapperChromeHeight + rootRect.height + 8
            )
          );

          if (
            !Number.isFinite(requiredHostHeight) ||
            requiredHostHeight <= 0
          ) {
            return;
          }

          host.style.height = `${requiredHostHeight}px`;
          host.style.minHeight = `${requiredHostHeight}px`;
        });
      });
    };

    applyMobileSizing();

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => applyMobileSizing())
        : null;

    if (resizeObserver && firstIncident) {
      resizeObserver.observe(firstIncident);
    }

    const handleMediaChange = () => applyMobileSizing();

    mediaQuery.addEventListener?.("change", handleMediaChange);
    window.addEventListener("resize", applyMobileSizing);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();

      mediaQuery.removeEventListener?.("change", handleMediaChange);
      window.removeEventListener("resize", applyMobileSizing);

      restoreHost();

      if (viewport) {
        viewport.style.height = "";
        viewport.style.minHeight = "";
      }

      root.style.height = "";
      root.style.minHeight = "";
    };
  }, [
    busy,
    error,
    incidents.length,
    isNarrow,
    pinnedLabel,
    resp,
  ]);

  return (
    <div
      ref={widgetRootRef}
      className="h-auto min-h-0 w-full overflow-visible md:h-full"
      style={{
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: 16,
        background: "#fff",
        padding: 16,
        fontFamily: bodyFont,
        boxSizing: "border-box",
        minWidth: 0,
      }}
    >
      <style>{`
        @media (max-width: 767px) {
          .vx-traffic-mobile-button {
            width: 100% !important;
            min-height: 44px;
            white-space: normal !important;
          }

          .vx-traffic-incidents-viewport {
            overflow-y: auto !important;
            overflow-x: hidden !important;
            overscroll-behavior-y: contain;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            scroll-snap-type: y mandatory;
          }

          .vx-traffic-incidents-viewport::-webkit-scrollbar {
            display: none;
          }

          .vx-traffic-incident-row {
            scroll-snap-align: start;
            scroll-snap-stop: always;
          }
        }
      `}</style>
      <div
        style={{
          display: "flex",
          alignItems: isNarrow ? "stretch" : "flex-start",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          flexDirection: isNarrow ? "column" : "row",
        }}
      >
        <div>
          <div style={{ fontFamily: headerFont, fontSize: 22, fontWeight: 900, color: accent, lineHeight: 1.1 }}>
            Traffic Intelligence
          </div>
          <div style={{ fontSize: 13, color: "rgba(0,0,0,0.55)", marginTop: 4 }}>
            Live traffic and incident impact for pinned location
          </div>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
            justifyContent: isNarrow ? "flex-start" : "flex-end",
          }}
        >
          {resp?.assessment?.status ? <span style={chipStyle(statusTone)}>{statusTone.label}</span> : liveBadge}
          <button
            type="button"
            className="vx-traffic-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchTraffic({ cacheBust: false })}
            disabled={busy}
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ height: 1, background: "rgba(0,0,0,0.08)", marginTop: 14, marginBottom: 14 }} />

      <div
        style={{
          borderRadius: 18,
          background: "rgba(0,0,0,0.03)",
          border: "1px solid rgba(0,0,0,0.06)",
          padding: 14,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isNarrow ? "1fr" : "1fr 160px 160px",
            gap: 12,
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Pinned Location
            </div>
            <input style={inputStyle} value={pinnedLabel} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Latitude
            </div>
            <input style={inputStyle} value={fmtNum(loc?.lat, 6)} readOnly disabled />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)", marginBottom: 6 }}>
              Longitude
            </div>
            <input style={inputStyle} value={fmtNum(loc?.lon, 6)} readOnly disabled />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          <button
            type="button"
            className="vx-traffic-mobile-button"
            style={generalBtnStyle}
            {...generalHandlers}
            onClick={() => setOpenPicker(true)}
          >
            Pick Location (Map)
          </button>

          <button
            type="button"
            className="vx-traffic-mobile-button"
            style={actionBtnStyle}
            {...actionHandlers}
            onClick={() => fetchTraffic({ cacheBust: true })}
            disabled={busy}
          >
            Cache Bust
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: 14,
          borderRadius: 22,
          border: "1px solid rgba(0,0,0,0.08)",
          background: "#fff",
          padding: 16,
          boxShadow: "0 8px 20px rgba(0,0,0,0.04)",
        }}
      >
        {error ? (
          <div style={{ color: "#b71c1c", fontWeight: 900, fontSize: 12 }}>{error}</div>
        ) : !resp ? (
          <div style={{ color: "rgba(0,0,0,0.55)", fontWeight: 900, fontSize: 12 }}>
            No traffic intelligence loaded yet.
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isNarrow ? "1fr" : "minmax(0, 1fr) 280px",
                gap: 12,
                alignItems: "stretch",
              }}
            >
              <div
                style={{
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 14,
                }}
              >
                <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <TrafficSignalIcon status={assessmentStatus} size={60} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Summary</div>
                    <div
                      style={{
                        fontSize: isNarrow ? 18 : 22,
                        fontWeight: 900,
                        color: "rgba(0,0,0,0.82)",
                        marginTop: 6,
                        lineHeight: 1.25,
                      }}
                    >
                      {safeText(assessment?.headline || resp?.summary, "No traffic summary returned")}
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                      <span style={chipStyle(statusTone)}>{statusTone.label}</span>

                      <span
                        style={chipStyle({
                          bg: "rgba(17, 24, 39, 0.06)",
                          border: "rgba(17, 24, 39, 0.12)",
                          text: "#111827",
                        })}
                      >
                        Confidence: {safeText(assessment?.confidence, "unknown")}
                      </span>

                      <span
                        style={chipStyle({
                          bg: "rgba(99, 102, 241, 0.08)",
                          border: "rgba(99, 102, 241, 0.22)",
                          text: "#4338ca",
                        })}
                      >
                        Recommendation: {safeText(assessment?.recommendation, "—")}
                      </span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 14, display: "grid", gap: 6 }}>
                  {Array.isArray(assessment?.reasons) && assessment.reasons.length ? (
                    assessment.reasons.slice(0, 4).map((reason, idx) => (
                      <div key={idx} style={{ fontSize: 13, color: "rgba(0,0,0,0.74)", lineHeight: 1.45 }}>
                        • {reason}
                      </div>
                    ))
                  ) : (
                    <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)" }}>
                      No operational reasons returned.
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Meta</div>
                <div style={{ fontSize: 14, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 4 }}>
                  {formatDateTimeGB(resp?.generated_at)}
                </div>

                <div style={{ marginTop: 10, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Source: {safeText(resp?.source, "tomtom")}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Settings: {safeText(resp?.settings_source, "unknown")}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Search radius: {isFiniteNumber(assessment?.incident_search_radius_km) ? `${Number(assessment.incident_search_radius_km).toFixed(2)} ${unit === "mph" ? "km" : "km"}` : "—"}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Surfaced incidents: {incidents.length}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Total relevant incidents: {isFiniteNumber(resp?.incident_total_count) ? Number(resp.incident_total_count) : isFiniteNumber(assessment?.incident_total_count) ? Number(assessment.incident_total_count) : incidents.length}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns: isNarrow ? "1fr" : "repeat(2, minmax(280px, 1fr))",
                gap: 12,
              }}
            >
              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Nearest Flow Segment</div>
                <div style={{ fontSize: 16, fontWeight: 900, color: "rgba(0,0,0,0.82)", marginTop: 6 }}>
                  {safeText(traffic?.road_display, "Nearest road segment")}
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
                  {traffic?.road_class ? (
                    <span
                      style={chipStyle({
                        bg: "rgba(17, 24, 39, 0.06)",
                        border: "rgba(17, 24, 39, 0.12)",
                        text: "#111827",
                      })}
                    >
                      {safeText(traffic?.road_class)}
                    </span>
                  ) : null}

                  {traffic?.road_class_label ? (
                    <span
                      style={chipStyle({
                        bg: "rgba(99, 102, 241, 0.08)",
                        border: "rgba(99, 102, 241, 0.22)",
                        text: "#4338ca",
                      })}
                    >
                      {safeText(traffic?.road_class_label)}
                    </span>
                  ) : null}
                </div>

                <div style={{ marginTop: 10, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Current speed: {formatSpeed(traffic?.current_speed, unit)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Free flow speed: {formatSpeed(traffic?.free_flow_speed, unit)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Segment delay: {formatDelay(traffic?.delay_display_text || traffic?.delay_sec)}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, color: "rgba(0,0,0,0.70)" }}>
                  Congestion ratio: {isFiniteNumber(traffic?.congestion_ratio) ? Number(traffic.congestion_ratio).toFixed(2) : "—"}
                </div>
              </div>

              <div
                style={{
                  borderRadius: 18,
                  background: "#fff",
                  border: "1px solid rgba(0,0,0,0.08)",
                  padding: 14,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>Primary Incident</div>
                {assessment?.primary_incident ? (
                  <IncidentRow incident={assessment.primary_incident} bodyFont={bodyFont} />
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)", marginTop: 6 }}>
                    No primary incident identified.
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                borderRadius: 18,
                background: "#fff",
                border: "1px solid rgba(0,0,0,0.08)",
                padding: 14,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 900, color: "rgba(0,0,0,0.55)" }}>
                  Surfaced Incidents
                </div>
                <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: 800 }}>
                  Showing {incidents.length} of{" "}
                  {isFiniteNumber(resp?.incident_total_count)
                    ? Number(resp.incident_total_count)
                    : isFiniteNumber(assessment?.incident_total_count)
                    ? Number(assessment.incident_total_count)
                    : incidents.length}
                </div>
              </div>

              <div
                ref={incidentsViewportRef}
                className="vx-traffic-incidents-viewport"
                style={{
                  marginTop: 10,
                  display: "grid",
                  gap: 10,
                }}
              >
                {incidents.length ? (
                  incidents.map((incident, index) => (
                    <IncidentRow
                      key={incident?.id || `${incident?.from || "x"}-${incident?.to || "y"}-${incident?.start_time || ""}`}
                      incident={incident}
                      bodyFont={bodyFont}
                      rowRef={index === 0 ? firstIncidentRowRef : null}
                    />
                  ))
                ) : (
                  <div style={{ fontSize: 13, color: "rgba(0,0,0,0.60)" }}>
                    No relevant incidents surfaced for this location.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {openPicker ? (
        <React.Suspense fallback={null}>
          <LazyLocationEditModal
            open={openPicker}
        onClose={() => setOpenPicker(false)}
        onCreated={async (created) => {
          try {
            const resolved = await resolveCreatedLocation(created);
            if (!resolved) {
              setOpenPicker(false);
              setError("Location was created, but coordinates could not be resolved from the response.");
              return;
            }
            setLoc(resolved);
            writeSharedLocation(resolved);
            setOpenPicker(false);
          } catch (e) {
            setOpenPicker(false);
            setError(formatApiError(e));
          }
        }}
            initial={{
              name: loc?.name,
              lat: loc?.lat,
              lon: loc?.lon,
              timezone: "Europe/London",
              country: "United Kingdom",
            }}
          />
        </React.Suspense>
      ) : null}
    </div>
  );
};

export default TrafficIntelligenceWidget;